import { useState, useEffect, Fragment } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from '../ApplicationState';
import SpinnerOverlay from '../SpinnerOverlay';
import CheckInTool from '../components/CheckInTool';
import Button from '../components/Button';
import CheckInToolMobile from '../components/CheckInToolMobile';
import StudentHeroSegment from '../components/StudentHeroSegment';
import ReviewCheckIn from './ReviewCheckIn';
import CheckinLimitModal from './CheckinLimitModal';
import {
  listStudentAssignments,
  recordMiddleSchoolJournalEntry,
  retrieveAssignment,
  submitAssignment,
} from '../BackendInterface';
import { getMiddleSchoolJournals } from '../BackendInterface';
import { setLocalStorage, getLocalStorage, removeLocalStorage } from '../aaa';
import { isCheckInModule } from '../GlobalFunctions';
import { selectClassroom } from '../Classrooms';
import { Assignment } from '../../peekapak-types/LessonPlanTypes';
import { State, play, setMedia } from '../components/AudioPlayer/AudioPlayer';
import { Icon } from '@iconify/react';
import toDoHeader from '../images/ToDo_Page.png';
import toDoHeaderHS from '../images/ToDo_Page_HS.png';
import styles from './CheckIn.module.scss';

interface LocationState extends Location {
  status: string;
}

/* ================= RATE LIMITING CONSTANTS & HELPERS ================= */

const MAX_SUBMISSIONS_PER_HOUR = 3;
const getRateLimitKey = (userId: string): string => `checkinSubmissionTimestamps:${userId}`;

/**
 * Retrieve the list of timestamp numbers from localStorage.
 * These represent previous submission times.
 */
const getSubmissionTimestamps = (userId: string): number[] => {
  try {
    const raw = window.localStorage.getItem(getRateLimitKey(userId));
    if (!raw) return [];
    return JSON.parse(raw) as number[];
  } catch {
    return [];
  }
};

/**
 * Save the array of timestamp numbers to localStorage.
 * @param timestamps - array of submission timestamps (in ms)
 */
const saveSubmissionTimestamps = (userId: string, timestamps: number[]): void => {
  try {
    window.localStorage.setItem(getRateLimitKey(userId), JSON.stringify(timestamps));
  } catch (err) {
    console.warn('Failed to save submission timestamps', err);
  }
};

/**
 * Determine if the user can submit a new emotion,
 * based on number of submissions within the last rolling hour.
 */
const canSubmitEmotion = (userId: string): boolean => {
  const now = Date.now();
  const oneHourAgo = now - 60 * 60 * 1000;

  const recent = getSubmissionTimestamps(userId).filter((ts) => ts > oneHourAgo);

  saveSubmissionTimestamps(userId, recent);
  return recent.length < MAX_SUBMISSIONS_PER_HOUR;
};

/**
 * Record a new submission timestamp in localStorage.
 */
const recordEmotionSubmission = (userId: string): void => {
  const timestamps = getSubmissionTimestamps(userId).slice(-10);
  timestamps.push(Date.now());
  saveSubmissionTimestamps(userId, timestamps);
};

/* ===================================================================== */

const CheckIn = (): JSX.Element => {
  const [isJournalEntriesLoaded, setIsJournalEntriesLoaded] = useState<boolean>(false);
  const [isCheckInAssignmentLoaded, setIsCheckInAssignmentLoaded] = useState<boolean>(false);
  const [isAssignmentSubmissionLoaded, setIsAssignmentSubmissionLoaded] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string>('');
  // isRateLimited - indicates if user hit submission limit
  const [isRateLimited, setIsRateLimited] = useState<boolean>(false);

  // isCheckingIn - show the check in tool
  const [isCheckingIn, setIsCheckingIn] = useState<boolean>(false);
  // isReviewing - show the response for the daily check in OR student's submission for the check in assignment
  const [isReviewing, setIsReviewing] = useState<boolean>(false);
  // isViewingSubmission - show student's submission for the check in assignment
  const [isViewingSubmission, setIsViewingSubmission] = useState<boolean>(false);
  const [toDoHeaderImage, setToDoHeaderImage] = useState<string>('');
  const [selectedEmotion, setSelectedEmotion] = useState<string>('');
  const [journalEntries, setJournalEntries] = useState<string[]>([]);
  const [checkInAssignment, setCheckInAssignment] = useState<Assignment | undefined>(undefined);
  const [assignmentSubmission, setAssignmentSubmission] = useState<string[] | undefined>(undefined);
  const [timeSubmitted, setTimeSubmitted] = useState<number | undefined>(undefined);

  const { userId, media, isPlayerLoading } = useSelector((state: RootState) => ({
    userId: state.user.userProfile?.userId ? state.user.userProfile.userId : '',
    media: state.audioPlayer.media,
    isPlayerLoading: state.audioPlayer.state === State.loading,
  }));

  const classroom = useSelector((state: RootState) => {
    return selectClassroom(state);
  });

  const userProfile = useSelector((state: RootState) => {
    return state.user.userProfile;
  });

  const language: string = classroom?.language || 'en';
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const isSmall = useMediaQuery({ query: '(max-width: 820px)' });

  /**
   * Set the correct To-Do header image based on user type and grade.
   */
  const getToDoHeaderImage = async (): Promise<void> => {
    try {
      if (userProfile && userProfile.type === 'Teacher' && classroom) {
        classroom.grade > 8 ? setToDoHeaderImage(toDoHeaderHS) : setToDoHeaderImage(toDoHeader);
      } else if (userProfile && userProfile.type === 'Student') {
        userProfile.grade > 8 ? setToDoHeaderImage(toDoHeaderHS) : setToDoHeaderImage(toDoHeader);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Check if currently loaded media is the "Check-In" audio.
   */
  const isCurrentStoryMediaLoaded = (): boolean => media?.title === 'Check-In';

  /**
   * Dispatch action to set the "Check-In" audio media.
   */
  const setCheckInMedia = (): void => {
    dispatch(
      setMedia({
        sources: {
          mp3: 'https://peekaplatform.s3.amazonaws.com/assets/premium/storyAudioMiddleSchool/Check-In-Wheel.wav',
        },
        title: 'Check-In',
      }),
    );
  };

  /**
   * Play the check-in audio if not already loaded.
   */
  const handlePlayAudio = (): void => {
    if (!isCurrentStoryMediaLoaded()) {
      setCheckInMedia();
    }
    dispatch(play());
  };

  /**
   * Store a localStorage flag that user completed or opted out of the check-in,
   * which expires at the end of the current day.
   */
  const storeIsCompletedOrOptedOutFlag = (): void => {
    const now: Date = new Date();
    const hours: number = now.getHours();
    const minutes: number = now.getMinutes();
    const minutesPassed: number = hours * 60 + minutes;
    const minutesPerDay: number = 24 * 60;
    const minutesRemaining: number = minutesPerDay - minutesPassed;
    setLocalStorage('isCheckInCompleteOrOptedOut', 'true', minutesRemaining);
  };

  /**
   * Handle when a user clicks an emotion from the wheel.
   * Resets error and rate-limit flags.
   */
  const handleEmotionClick = (emotion: string): void => {
    setSelectedEmotion(emotion);
    setIsError(false);
    setIsRateLimited(false);
  };

  /**
   * Navigate user back to their To-Do list.
   */
  const handleGoHome = (): void => {
    history.push('/studentportal/toDos');
  };

  /**
   * Reset the submission state to allow user to submit again.
   */
  const handleResetSubmission = (): void => {
    setIsCheckingIn(true);
    setIsReviewing(false);
    setSelectedEmotion('');
    removeLocalStorage('isCheckInCompleteOrOptedOut');
  };

  /**
   * Handle when user chooses to skip today's check-in.
   */
  const handleSkipToday = (): void => {
    storeIsCompletedOrOptedOutFlag();
    history.push('/studentportal/toDos');
  };

  /**
   * Retrieve a student's assignment submission from backend.
   */
  const retrieveAssignmentSubmission = async (userId: string, createdAt: number): Promise<void> => {
    try {
      const toDoPayload = await retrieveAssignment(userId, createdAt);
      if (toDoPayload && toDoPayload[0] && toDoPayload[0].work && toDoPayload[0].work.content) {
        const content: string[] = toDoPayload[0].work.content;
        setAssignmentSubmission(content);
        setTimeSubmitted(toDoPayload[0].modifiedAt);
      }
    } catch {
      console.error('could not retrieve check in assignment');
    } finally {
      setIsAssignmentSubmissionLoaded(true);
    }
  };

  /**
   * Retrieve journal entries for the current user from backend.
   */
  const retrieveJournalEntries = async (): Promise<void> => {
    try {
      const journalEntriesData = await getMiddleSchoolJournals(userId);
      const now: Date = new Date();
      const today: Date = new Date(now.setHours(0, 0, 0, 0));
      const emotions: string[] = journalEntriesData.reduce((acc: string[], curr) => {
        if (new Date(curr.createdAt) > today) acc.push(curr.feeling);
        return acc;
      }, []);
      setJournalEntries(emotions);
      setLoadErrorMessage('');
    } catch (error) {
      console.error('Error fetching check-in journal history', error);
      setLoadErrorMessage(
        'We could not load your previous check-ins right now. You can still submit your current check-in.',
      );
    } finally {
      setIsJournalEntriesLoaded(true);
    }
  };

  /**
   * Submit the selected journal entry after validation and rate limit check.
   */
  const submitJournalEntry = async (): Promise<void> => {
    setIsShowSpinner(true);

    try {
      if (!selectedEmotion) {
        setIsError(true);
        return;
      }

      // Rate-limit check first
      if (!canSubmitEmotion(userId)) {
        setIsRateLimited(true);
        return;
      }

      // Record journal entry in backend
      await recordMiddleSchoolJournalEntry(userId, selectedEmotion, '', false);

      // Record submission once, after success
      recordEmotionSubmission(userId);

      storeIsCompletedOrOptedOutFlag();

      journalEntries.push(selectedEmotion);

      if (checkInAssignment && checkInAssignment.status !== 'submitted') {
        const content: string[] = [selectedEmotion];
        await submitAssignment(userId, checkInAssignment.createdAt, content);
        setAssignmentSubmission(content);
        setIsViewingSubmission(true);
      } else {
        setIsViewingSubmission(false);
      }

      setIsCheckingIn(false);
      setIsReviewing(true);
    } catch (err) {
      console.error('could not submit emotion to database', err);
    } finally {
      setIsShowSpinner(false);
    }
  };

  /**
   * Retrieve the student's check-in assignment to display.
   */
  const getCheckInAssignment = async (): Promise<void> => {
    setIsShowSpinner(true);
    try {
      if (userProfile) {
        const studentToDos = await listStudentAssignments(userId, language, userProfile.grade);
        const toDo = studentToDos.find((toDo) => isCheckInModule(toDo.todo));
        if (toDo) {
          setCheckInAssignment(toDo);
          await retrieveAssignmentSubmission(userId, toDo.createdAt);
        }
      }
    } catch {
      console.error('Error fetching student ToDos');
    } finally {
      setIsCheckInAssignmentLoaded(true);
      setIsShowSpinner(false);
    }
  };

  /**
   * Determines if user should see the check-in tool or review view based on state.
   */
  const CheckIsCheckingIn = (): void => {
    // if is daily check in, and hasn't submitted any journal entry for today
    // OR
    // if is check in assignment, and hasn't submitted
    if ((!location.state && !journalEntries) || (location.state && location.state.status !== 'submitted')) {
      setIsCheckingIn(true);
      setIsReviewing(false);
    }
  };

  const CheckIsReviewing = (): void => {
    // if is daily check in, and has already checked in for today
    // OR
    // if is check in assignment, and has already submitted
    if ((!location.state && journalEntries.length > 0) || (location.state && location.state.status === 'submitted')) {
      setIsReviewing(true);
      setIsCheckingIn(false);
    }
  };

  /* ------------ useEffect hooks to control component lifecycle ------------ */

  useEffect(() => {
    if (userProfile && userProfile.type === 'Teacher') {
      setIsLoaded(true);
    } else {
      if (!isJournalEntriesLoaded) retrieveJournalEntries();
      if (!isCheckInAssignmentLoaded || !isAssignmentSubmissionLoaded) getCheckInAssignment();
      if (isJournalEntriesLoaded && isCheckInAssignmentLoaded && isAssignmentSubmissionLoaded) {
        setIsLoaded(true);
      }
    }
  }, [isJournalEntriesLoaded, isCheckInAssignmentLoaded, isAssignmentSubmissionLoaded]);

  useEffect(() => {
    const hasCheckedIn = getLocalStorage('isCheckInCompleteOrOptedOut');
    if (hasCheckedIn) {
      setIsCheckingIn(false);
      setIsReviewing(true);
    } else {
      setIsCheckingIn(true);
      setIsReviewing(false);
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      CheckIsCheckingIn();
      CheckIsReviewing();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (location.state && location.state.status === 'submitted' && assignmentSubmission) {
      setIsViewingSubmission(true);
    }
  }, [isViewingSubmission, location.state, assignmentSubmission]);

  useEffect(() => {
    if (!toDoHeaderImage) {
      getToDoHeaderImage();
    }
  }, [classroom, userProfile, toDoHeaderImage]);

  /* --------------------------- JSX Render --------------------------- */

  return (
    <>
      <SpinnerOverlay isShow={isShowSpinner || !toDoHeaderImage} />
      <StudentHeroSegment home backgroundImage={toDoHeaderImage}>
        <div className={styles.heroText}>
          <h1>Check In</h1>
          <h3>{`Check in with how you're feeling today`}</h3>
        </div>
      </StudentHeroSegment>
      <div className={styles.container}>
        {loadErrorMessage && (
          <p
            role='alert'
            className='mb-[1.6rem] rounded-[0.6rem] bg-[#FDECEC] px-[1.2rem] py-[1rem] text-[1.4rem] text-[#A12D2F]'
          >
            {loadErrorMessage}
          </p>
        )}
        {isCheckingIn && (
          <Fragment key={'emotion wheel'}>
            <div className={styles.titleRow}>
              <h4>How are you feeling today?</h4>
              <button onClick={handlePlayAudio} disabled={isPlayerLoading}>
                <Icon className={styles.playIcon} icon='ooui:volume-up-ltr' />
              </button>
            </div>
            <p>Take a moment to reflect on your feelings, then select an emotion from the wheel below.</p>
            {isSmall ? (
              <CheckInToolMobile handleEmotionClick={handleEmotionClick} selectedEmotion={selectedEmotion} />
            ) : (
              <CheckInTool handleEmotionClick={handleEmotionClick} selectedEmotion={selectedEmotion} />
            )}
            <p>
              {selectedEmotion ? selectedEmotion : <span>&nbsp;&nbsp;</span>}
              {isError && 'Please select an emotion before submitting'}
              {isRateLimited && (
                <CheckinLimitModal
                  isShow={isRateLimited}
                  onHide={() => setIsRateLimited(false)}
                  onOkay={() => setIsRateLimited(false)}
                />
              )}
            </p>
            <Button thinText darkBlue small onClick={submitJournalEntry}>
              Submit
            </Button>
            <div className={styles.skipEntry} onClick={handleSkipToday}>
              Skip For Today
            </div>
          </Fragment>
        )}
        {isReviewing && (
          <ReviewCheckIn
            studentWork={assignmentSubmission}
            journalEntries={journalEntries}
            handleResetSubmission={handleResetSubmission}
            handleGoHome={handleGoHome}
            isViewDailyCheckIn={!location.state && !isViewingSubmission}
            isViewAssignmentSubmission={isViewingSubmission}
            timeSubmitted={timeSubmitted}
          />
        )}
      </div>
    </>
  );
};

export default CheckIn;
